import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

const ADMIN_MENU_GROUPS = [
  {
    key: 'content',
    title: '記事運用',
    description: '記事の作成、承認、掲載導線の管理をまとめています。',
    accent: 'content',
    quickLinks: [
      {
        title: '記事一覧',
        description: '公開側の記事一覧を別導線で確認します。',
        to: '/posts',
      },
      {
        title: '生産の可視化',
        description: '農家近況報告につながる生産の可視化ページを確認します。',
        to: '/visibility',
      },
    ],
    items: [
      {
        title: '記事作成',
        description: '運営記事と農家近況報告の下書き保存、本文編集、投稿申請を行います。',
        to: '/admin/reports',
        tag: 'EDITOR',
      },
      {
        title: '記事承認',
        description: '申請された運営記事と農家近況報告の承認、差し戻しを行います。',
        to: '/admin/review',
        tag: 'REVIEW',
      },
      {
        title: 'ヘッダー記事',
        description: 'ヘッダーに流す記事の選定、表示件数、優先順を設定します。',
        to: '/admin/header-ticker',
        tag: 'HEADER',
      },
    ],
  },
  {
    key: 'users',
    title: 'ユーザー管理',
    description: '農家、ドクター、登録ユーザー情報の確認と管理をまとめています。',
    accent: 'users',
    items: [
      {
        title: 'ユーザー一覧',
        description: '農家とドクターの登録状況を一覧で確認できます。',
        to: '/admin/users',
        tag: 'INDEX',
      },
      {
        title: 'ユーザー情報修正',
        description: '登録済みユーザーの氏名、連絡先、役職を更新します。',
        to: '/admin/accounts?tab=users',
        tag: 'PROFILE',
      },
      {
        title: 'サイト管理者管理',
        description: 'サイト管理者アカウントの一覧確認と情報更新を行います。',
        to: '/admin/site-admins',
        tag: 'ADMIN',
      },
      {
        title: 'アカウント作成',
        description: 'サイト管理者、ドクター、農家アカウントの新規作成を行います。',
        to: '/admin/accounts?tab=create',
        tag: 'ACCOUNT',
      },
      {
        title: '紐付け（農家、ドクター）',
        description: '農家・ドクターアカウントを公開ページに紐付けます。',
        to: '/admin/accounts?tab=link',
        tag: 'LINK',
      },
      {
        title: 'パスワード確認',
        description: '保存済みパスワードの確認と閲覧ログの確認を行います。',
        to: '/admin/accounts?tab=check',
        tag: 'PASSWORD',
      },
    ],
  },
  {
    key: 'settings',
    title: '全体設定',
    description: 'サイト全体で共通となる設定をまとめています。',
    accent: 'content',
    items: [
      {
        title: '設定',
        description: '管理者自身のパスワード変更や通知先設定など、共通設定を行います。',
        to: '/admin/settings',
        tag: 'CONFIG',
      },
    ],
  },
];

export default function AdminMenuPage() {
  const { isAdmin, canManageOperatorPosts, canManageFarmerReports, canReviewArticles, roleLabel } = useAuth();
  const visibleGroups = ADMIN_MENU_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => {
      if (item.to === '/admin/reports') {
        return canManageOperatorPosts || canManageFarmerReports;
      }
      if (item.to === '/admin/review') {
        return canReviewArticles;
      }
      if (
        item.to === '/admin/settings'
        || item.to === '/admin/site-admins'
        || item.to.startsWith('/admin/accounts')
      ) {
        return isAdmin;
      }
      return true;
    }),
  })).filter((group) => group.items.length > 0);

  return (
    <div className="admin-menu-page">
      <Helmet><title>Drs.Farm | 管理者メニュー</title></Helmet>
      <section className="admin-menu-hero">
        <div className="admin-menu-hero-copy">
          <p className="admin-menu-kicker">Operations Dashboard</p>
          <h1>管理者メニュー</h1>
          <p className="admin-menu-lead">記事運用・ユーザー管理・全体設定を、目的別にすぐ辿れるように整理しています。</p>
        </div>
        <div className="admin-menu-hero-side">
          <div className="admin-menu-role-card">
            <span className="admin-menu-role-label">現在の権限</span>
            <strong>{roleLabel}</strong>
            <p>承認、設定、アカウント確認などの管理導線をここから開けます。</p>
          </div>
        </div>
      </section>

      <div className="admin-menu-sections">
        {visibleGroups.map((group) => (
          <section key={group.key} className={`admin-menu-section admin-menu-section-${group.accent}`}>
            <div className="admin-menu-group-heading">
              <div>
                <p className="admin-menu-group-kicker">
                  {group.key === 'content' ? 'CONTENT' : group.key === 'users' ? 'USERS' : 'SETTINGS'}
                </p>
                <h2>{group.title}</h2>
              </div>
              <p>{group.description}</p>
            </div>

            {group.quickLinks?.length ? (
              <div className="admin-menu-quick-links">
                {group.quickLinks.map((item) => (
                  <Link key={item.to} className="admin-menu-quick-link" to={item.to}>
                    <span>{item.title}</span>
                    <small>{item.description}</small>
                  </Link>
                ))}
              </div>
            ) : null}

            <div className="admin-menu-grid">
              {group.items.map((item) => (
                <article key={item.to} className="admin-menu-card">
                  <div className="admin-menu-card-top">
                    <span className="admin-menu-card-tag">{item.tag}</span>
                    <span className="admin-menu-card-arrow" aria-hidden="true">→</span>
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  <Link className="admin-menu-link" to={item.to}>ページを開く</Link>
                </article>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
